import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { SignedIn, SignedOut, SignInButton, SignOutButton, useUser, useAuth } from '@clerk/clerk-react';
import { Typography, Button, Alert, CircularProgress } from '@mui/material';
import AdminPage from './pages/AdminPage';
import GamePage from './pages/GamePage';
import SceneCanvasPage from './pages/SceneCanvasPage';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

async function fetchWithTimeout(url, options = {}, ms = 20000) {
  const controller = new AbortController();
  const tid = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(tid);
  }
}

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: '#8b5cf6' },
    secondary: { main: '#e879f9' },
    background: { default: '#09090b', paper: '#18181b' },
    divider: 'rgba(255,255,255,0.08)'
  },
  typography: {
    fontSize: 13,
    fontFamily: '"Inter", "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h3: { fontSize: '1.5rem', fontWeight: 600 },
    h5: { fontSize: '1.05rem', fontWeight: 600 },
    h6: { fontSize: '0.95rem', fontWeight: 600 },
    body1: { fontSize: '0.8125rem' },
    body2: { fontSize: '0.8125rem' }
  },
  shape: { borderRadius: 8 },
  transitions: {
    duration: {
      shortest: 75,
      shorter: 100,
      short: 125,
      standard: 150,
      complex: 200,
      enteringScreen: 150,
      leavingScreen: 100
    }
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' }
      }
    },
    MuiButton: {
      defaultProps: { size: 'small' },
      styleOverrides: { root: { textTransform: 'none', fontWeight: 500 } }
    },
    MuiTextField: { defaultProps: { size: 'small' } },
    MuiFormControl: { defaultProps: { size: 'small' } },
    MuiChip: { defaultProps: { size: 'small' } }
  }
});

function AppContent() {
  const { user, isLoaded: userLoaded } = useUser();
  const { getToken } = useAuth();
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  // Fetch role from /me; bootstrap first admin only when role is still unset
  useEffect(() => {
    const fetchUserRole = async () => {
      if (!userLoaded || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = await getToken();

        const meRes = await fetchWithTimeout(`${API_BASE_URL}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (meRes.ok) {
          const userData = await meRes.json();
          const role = userData.role;
          if (role === 'admin' || role === 'member') {
            setUserRole(role);
            return;
          }

          // No role yet — only then try first-admin bootstrap
          const initRes = await fetchWithTimeout(`${API_BASE_URL}/api/users/initialize-first-admin`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          if (initRes.ok) {
            const initData = await initRes.json();
            setUserRole(
              initData.role === 'admin' || initData.role === 'member' ? initData.role : 'admin'
            );
            return;
          }
          // 403: admin already exists, this user still has no role — expected, no console noise
          setUserRole(null);
          return;
        }

        console.error('Failed to fetch user role:', meRes.status);
        setUserRole(null);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole(null);
        if (error?.name === 'AbortError') {
          console.warn('Role fetch timed out — check API / network');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserRole();
  }, [user, userLoaded, getToken]);

  if (!userLoaded || loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-zinc-950 text-zinc-300">
        <CircularProgress size={28} sx={{ color: '#a78bfa' }} />
        <p className="text-sm">Loading…</p>
      </div>
    );
  }

  // Check if user has access (admin or member role)
  const hasAccess = userRole === 'admin' || userRole === 'member';

  return (
    <>
      {hasAccess ? (
        <Routes>
          <Route path="/scenes/:sceneId/canvas" element={<SceneCanvasPage userRole={userRole} />} />
          <Route path="*" element={<AdminPage userRole={userRole} />} />
        </Routes>
      ) : (
        <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-950">
          <div className="w-full max-w-md rounded-xl border border-zinc-800 bg-zinc-900/60 p-6 shadow-2xl shadow-black/40">
            <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(59,130,246,0.12)', color: '#e4e4e7' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ color: '#fafafa' }}>
                Access pending
              </Typography>
              <Typography variant="body2" sx={{ color: '#a1a1aa' }}>
                Your account is waiting for approval. An administrator needs to grant you member access.
              </Typography>
            </Alert>
            <p className="text-xs text-zinc-500 mb-4">Contact an administrator to get access.</p>
            <Button
              variant="outlined"
              fullWidth
              onClick={async () => {
              try {
                const token = await getToken();
                const response = await fetch(`${API_BASE_URL}/api/users/initialize-first-admin`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                if (response.ok) {
                  window.location.reload();
                } else {
                  const error = await response.json();
                  alert(error.message || 'Failed to initialize admin');
                }
              } catch (err) {
                alert('Error: ' + err.message);
              }
            }}
          >
              Try initialize as first admin
            </Button>
            <SignOutButton>
              <Button variant="text" fullWidth sx={{ mt: 1.5 }} color="inherit">
                Log out
              </Button>
            </SignOutButton>
          </div>
        </div>
      )}
    </>
  );
}

function AdminGate() {
  return (
    <>
      <SignedOut>
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 bg-zinc-950">
          <div className="text-center space-y-2">
            <h1 className="text-xl font-semibold tracking-tight text-zinc-100">Velvet Room</h1>
            <p className="text-sm text-zinc-500">Sign in to open the admin panel</p>
          </div>
          <SignInButton mode="modal">
            <button
              type="button"
              className="rounded-lg bg-violet-600 px-5 py-2.5 text-sm font-medium text-white shadow-lg shadow-violet-900/30 transition hover:bg-violet-500"
            >
              Sign in
            </button>
          </SignInButton>
        </div>
      </SignedOut>
      <SignedIn>
        <AppContent />
      </SignedIn>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route path="/game" element={<GamePage />} />
          <Route path="/*" element={<AdminGate />} />
        </Routes>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App; 